import React, { useContext, useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { BASE_URL, RoleEnum } from "../../Constants";
import InputWithVisibility from "../settings/InputWithVisibility";
import TextStyleControl from "../settings/TextStyleControl";
import ImageCustomizationDialog from "../employee/ImageCustomizationDialog";
import CardPreview from "./CardPreview";
import { useTranslation } from "react-i18next";
import AppContext from "../../AppContext";
import { EditFormContext } from "../publicEmployees/PublicEmployeeEditForm";
import { EyeOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";

const CardCustomizer = ({
  isPublicEmployee = true,
  initialCardData = null,
  cardConfig = null,
  companyLogoUrl = null,
  handleCardSubmit,
  isReadOnly = false,
  socialMediaTab = null,
}) => {
  const { t } = useTranslation();
  const authCtx = useContext(AppContext);

  // Always call useContext, but only use values conditionally
  const editFormContext = useContext(EditFormContext);
  const contextCardData = isPublicEmployee ? editFormContext?.cardData : null;
  const contextSubmit = isPublicEmployee
    ? editFormContext?.handleCardSubmit
    : null;

  const [activeTab, setActiveTab] = useState("content");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [imageDialogue, setImageDialogue] = useState(false);
  const [cropFile, setCropFile] = useState(null);

  // Initialize card data based on source
  const getInitialCardData = () => {
    // Default card data structure
    return {
      companyLogoVisible: cardConfig?.companyLogoVisible ?? true,
      isCompanyLogo: cardConfig?.companyLogoVisible ?? true,
      companyLogoUrl: companyLogoUrl || "/future_cards_logo.png",
      profileImage: null,
      profileImageVisible: cardConfig?.profileImageVisible ?? true,
      backgroundImageUrl: cardConfig?.backgroundImageUrl ?? null,
      backgroundImage: null,
      name: contextCardData?.name || "John Doe",
      nameVisible: cardConfig?.nameVisible ?? true,
      designation: "Designation",
      designationVisible: cardConfig?.designationVisible ?? true,
      phone: "+123-456-7890",
      phoneVisible: cardConfig?.phoneVisible ?? true,
      email: "hello@example.com",
      emailVisible: cardConfig?.emailVisible ?? true,
      employeeId: "EMP123",
      employeeIdVisible: cardConfig?.employeeIdVisible ?? true,
      companyName: "Future Industries",
      companyNameVisible: cardConfig?.companyNameVisible ?? true,
      backgroundColor: cardConfig?.backgroundColor ?? "#000147",
      useBackgroundImage: cardConfig?.useBackgroundImage ?? false,
      qrCodeVisible: cardConfig?.qrCodeVisible ?? true,
      dividerColor: cardConfig?.dividerColor ?? "#e5e7eb",
      textStyles: {
        name: {
          color: cardConfig?.textStyles?.name?.color ?? "#ffffff",
          size: cardConfig?.textStyles?.name?.size ?? "18",
          bold: cardConfig?.textStyles?.name?.bold ?? true,
          italic: cardConfig?.textStyles?.name?.italic ?? false,
        },
        designation: {
          color: cardConfig?.textStyles?.designation?.color ?? "#ffffff",
          size: cardConfig?.textStyles?.designation?.size ?? "14",
          bold: cardConfig?.textStyles?.designation?.bold ?? false,
          italic: cardConfig?.textStyles?.designation?.italic ?? false,
        },
        phone: {
          color: cardConfig?.textStyles?.phone?.color ?? "#ffffff",
          size: cardConfig?.textStyles?.phone?.size ?? "12",
          bold: cardConfig?.textStyles?.phone?.bold ?? false,
          italic: cardConfig?.textStyles?.phone?.italic ?? false,
        },
        email: {
          color: cardConfig?.textStyles?.email?.color ?? "#ffffff",
          size: cardConfig?.textStyles?.email?.size ?? "12",
          bold: cardConfig?.textStyles?.email?.bold ?? false,
          italic: cardConfig?.textStyles?.email?.italic ?? false,
        },
        employeeId: {
          color: cardConfig?.textStyles?.employeeId?.color ?? "#ffffff",
          size: cardConfig?.textStyles?.employeeId?.size ?? "12",
          bold: cardConfig?.textStyles?.employeeId?.bold ?? false,
          italic: cardConfig?.textStyles?.employeeId?.italic ?? false,
        },
        companyName: {
          color: cardConfig?.textStyles?.companyName?.color ?? "#ffffff",
          size: cardConfig?.textStyles?.companyName?.size ?? "16",
          bold: cardConfig?.textStyles?.companyName?.bold ?? false,
          italic: cardConfig?.textStyles?.companyName?.italic ?? false,
        },
        titleColor: {
          color: cardConfig?.textStyles?.titleColor?.color ?? "#ffffff",
        },
      },
    };
  };

  const [cardData, setCardData] = useState();
  // Determine which cardData and setCardData to use
  // const cardData = formContext ? contextCardData : localCardData;
  // const setCardData = formContext ? contextSetCardData : setLocalCardData;

  useEffect(() => {
    if (!isPublicEmployee) {
      setCardData(getInitialCardData());
    } else if (isPublicEmployee && contextCardData) {
      setCardData(contextCardData);
    }
  }, []);

  // Helper functions
  const getTextStyle = (field) => {
    const defaultStyles = {
      color: "#ffffff",
      size: "14",
      bold: false,
      italic: false,
    };

    const fieldStyles = cardData?.textStyles?.[field] || defaultStyles;

    return {
      color: fieldStyles.color || defaultStyles.color,
      fontSize: `${fieldStyles.size || defaultStyles.size}px`,
      fontWeight: fieldStyles.bold ? "bold" : "normal",
      fontStyle: fieldStyles.italic ? "italic" : "normal",
    };
  };

  const handleChange = (name, value) => {
    setCardData((pre) => ({ ...pre, [name]: value }));
  };

  const handleImageUpload = (type, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleChange(type, file);
        handleChange(type + "Preview", reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfileImageChange = (type, e) => {
    if (e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      const fileSizeInKB = selectedFile.size / 1024;

      if (fileSizeInKB > 1024) {
        toast.error("Image size must be less than 1MB");
        e.target.value = "";
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        setCropFile(reader.result);
        // handleChange(type, selectedFile);
        // handleChange(type + "Preview", reader.result);
      };
      reader.readAsDataURL(selectedFile);

      setImageDialogue(true);

      e.target.value = null;
    }
  };

  const handleFileCropSave = (file) => {
    handleChange("profileImage", file);
    handleChange("profileImagePreview", URL.createObjectURL(file));
    setCropFile(null);
    setImageDialogue(false);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsSubmitting(true);
    if (isPublicEmployee && contextSubmit) {
      await contextSubmit(cardData);
    } else if (handleCardSubmit) {
      await handleCardSubmit(cardData);
    }
    setIsSubmitting(false);
  };

  // Return loading state if cardData is not yet available
  if (!cardData) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-gray-600">
          {t("common.loading") || "Loading..."}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex md:flex-row flex-col min-h-screen gap-8 md:p-6 p-2 bg-gray-50`}
    >
      {/* Image Cropping Dialog */}
      <ImageCustomizationDialog
        showDialog={imageDialogue}
        image={cropFile}
        onClose={() => setImageDialogue(false)}
        handleFileSave={handleFileCropSave}
      />

      {/* Left Side - Customization Panel */}
      <div className="md:w-1/2 w-full flex flex-col items-end">
        <div className="w-full">
          <div className="flex justify-between items-center mb-6">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setActiveTab("content")}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === "content"
                    ? "bg-blue-500 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                {t("digitalCard.tabs.content")}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("style")}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === "style"
                    ? "bg-blue-500 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                {t("digitalCard.tabs.style")}
              </button>
              {socialMediaTab && (
                <button
                  type="button"
                  onClick={() => setActiveTab("socialMedia")}
                  className={`px-4 py-2 rounded-lg ${
                    activeTab === "socialMedia"
                      ? "bg-blue-500 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {t("digitalCard.tabs.socialMedia")}
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => setIsPreviewOpen(true)}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <EyeOutlined />
              {t("buttons.preview")}
            </button>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm">
            {/* Hidden rather than removed, so unsaved links survive switching tabs. */}
            {socialMediaTab && (
              <div hidden={activeTab !== "socialMedia"}>{socialMediaTab}</div>
            )}

            {activeTab === "content" && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="companyLogoVisible"
                    checked={
                      cardData?.companyLogoVisible || cardData?.isCompanyLogo
                    }
                    onChange={(e) => {
                      handleChange("companyLogoVisible", e.target.checked);
                      handleChange("isCompanyLogo", e.target.checked);
                    }}
                    className="h-4 w-4"
                  />
                  <label
                    htmlFor="companyLogoVisible"
                    className="text-sm font-medium text-gray-700"
                  >
                    {t("digitalCard.content.showCompanyLogo")}
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("form.profileImage")}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleProfileImageChange("profileImage", e)
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <InputWithVisibility
                  label={t("form.name")}
                  field="name"
                  value={cardData?.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  cardData={cardData}
                  setCardData={setCardData}
                />
                <InputWithVisibility
                  label={t("form.role")}
                  field="designation"
                  value={cardData?.designation}
                  onChange={(e) => handleChange("designation", e.target.value)}
                  cardData={cardData}
                  setCardData={setCardData}
                />
                <InputWithVisibility
                  label={t("form.phone")}
                  field="phone"
                  value={cardData?.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  cardData={cardData}
                  setCardData={setCardData}
                />
                <InputWithVisibility
                  label={t("form.email")}
                  field="email"
                  value={cardData?.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  cardData={cardData}
                  setCardData={setCardData}
                />
                <InputWithVisibility
                  label={t("form.employeeID")}
                  field="employeeId"
                  value={cardData?.employeeId}
                  onChange={(e) => handleChange("employeeId", e.target.value)}
                  cardData={cardData}
                  setCardData={setCardData}
                />
                <InputWithVisibility
                  label={t("form.company")}
                  field="companyName"
                  value={cardData?.companyName}
                  onChange={(e) => handleChange("companyName", e.target.value)}
                  cardData={cardData}
                  setCardData={setCardData}
                />

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="qrCodeVisible"
                    checked={cardData?.qrCodeVisible}
                    onChange={(e) => {
                      handleChange("qrCodeVisible", e.target.checked);
                    }}
                    className="h-4 w-4"
                  />
                  <label
                    htmlFor="qrCodeVisible"
                    className="text-sm font-medium text-gray-700"
                  >
                    {t("digitalCard.content.showQRCode")}
                  </label>
                </div>
              </div>
            )}

            {activeTab === "style" && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t("digitalCard.style.background")}
                  </label>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        id="useColor"
                        checked={!cardData?.useBackgroundImage}
                        onChange={() =>
                          handleChange("useBackgroundImage", false)
                        }
                        className="h-4 w-4"
                      />
                      <label htmlFor="useColor">
                        {t("digitalCard.style.useColor")}
                      </label>
                    </div>
                    {!cardData?.useBackgroundImage && (
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={cardData?.backgroundColor || "#000147"}
                          onChange={(e) =>
                            handleChange("backgroundColor", e.target.value)
                          }
                          className="h-10 w-20"
                        />
                        <input
                          type="text"
                          value={cardData?.backgroundColor || "#000147"}
                          onChange={(e) =>
                            handleChange("backgroundColor", e.target.value)
                          }
                          className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    )}

                    <>
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          id="useImage"
                          checked={cardData?.useBackgroundImage || false}
                          onChange={() =>
                            handleChange("useBackgroundImage", true)
                          }
                          className="h-4 w-4"
                        />
                        <label htmlFor="useImage">Use Image</label>
                      </div>
                      {cardData?.useBackgroundImage && (
                        <div className="ml-6">
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            onChange={(e) =>
                              handleImageUpload("backgroundImage", e)
                            }
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      )}
                    </>
                  </div>
                </div>
                <TextStyleControl
                  label={t("form.name")}
                  field="name"
                  cardData={cardData}
                  setCardData={setCardData}
                />
                <TextStyleControl
                  label={t("form.role")}
                  field="designation"
                  cardData={cardData}
                  setCardData={setCardData}
                />
                <TextStyleControl
                  label={t("form.phone")}
                  field="phone"
                  cardData={cardData}
                  setCardData={setCardData}
                />
                <TextStyleControl
                  label={t("form.email")}
                  field="email"
                  cardData={cardData}
                  setCardData={setCardData}
                />
                <TextStyleControl
                  label={t("form.employeeID")}
                  field="employeeId"
                  cardData={cardData}
                  setCardData={setCardData}
                />
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("digitalCard.style.dividerLineStyle")}
                  </label>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-500 mb-1">
                        {t("digitalCard.style.color")}
                      </label>
                      <input
                        type="color"
                        value={cardData?.dividerColor || "#e5e7eb"}
                        onChange={(e) =>
                          handleChange("dividerColor", e.target.value)
                        }
                        className="h-8 w-16"
                      />
                    </div>
                  </div>
                </div>
                <TextStyleControl
                  label={t("form.company")}
                  field="companyName"
                  cardData={cardData}
                  setCardData={setCardData}
                />
                <div className="w-full">
                  <TextStyleControl
                    label={
                      <span className="w-full">
                        {t("form.titleColor")}
                        <Tooltip title={t("form.appliesOnlyForAppleWallet")}>
                          <InfoCircleOutlined
                            style={{
                              color: "#22c55e",
                              marginLeft: 6,
                              marginRight: 2,
                              paddingRight: 4,
                              cursor: "pointer",
                            }}
                          />
                        </Tooltip>
                      </span>
                    }
                    field="titleColor"
                    cardData={cardData}
                    setCardData={setCardData}
                    fontSize={false}
                    fontWeight={false}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* The Social Media tab saves separately, with its own button. */}
        {!isReadOnly && activeTab !== "socialMedia" && (
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            type="button"
            className="w-fit mt-6 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
          >
            {isSubmitting
              ? t("digitalCard.buttons.saving")
              : t("digitalCard.buttons.saveConfiguration")}
          </button>
        )}
      </div>

      {/* Right Side - Preview */}
      <div className="md:w-1/2 w-full">
        <div className="sticky md:ml-16 top-6">
          <h2 className="text-xl w-96 font-semibold mb-4 text-center">
            {t("digitalCard.preview")}
          </h2>
          <div
            style={{
              backgroundColor: !cardData?.useBackgroundImage
                ? cardData?.backgroundColor
                : undefined,
              backgroundImage:
                cardData?.useBackgroundImage &&
                (cardData?.backgroundImage || cardData?.backgroundImageUrl)
                  ? `url(${
                      cardData?.backgroundImagePreview ||
                      cardData?.backgroundImageUrl
                    })`
                  : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
            className="bg-white rounded-lg md:w-96 w-full shadow-lg overflow-hidden relative"
          >
            {/* Overlay div */}
            <div
              className="absolute inset-0 bg-black opacity-40"
              style={{
                backgroundColor: !cardData?.useBackgroundImage
                  ? cardData?.backgroundColor
                  : undefined,
              }}
            ></div>
            <div className="relative aspect-[2.4] w-full">
              {(cardData?.companyLogoVisible || cardData?.isCompanyLogo) && (
                <div className="flex justify-center mt-14">
                  <img
                    src={cardData?.companyLogoUrl || "/future_cards_logo.png"}
                    alt="Company Logo"
                    className="h-14 object-contain"
                    onError={(e) => {
                      e.target.src = "/future_cards_logo.png";
                    }}
                  />
                </div>
              )}
            </div>

            <div className="relative px-6 pb-6">
              <div className="absolute -top-16 left-1/2 -translate-x-1/2">
                <img
                  src={
                    cardData?.profileImagePreview ||
                    cardData?.profileImage ||
                    (cardData?.gender === "Female" ? `/user_placeholder_women.png` : `/user-placeholder.png`)
                  }
                  alt="Profile"
                  className="w-32 h-32 rounded-full border-4 border-white object-cover"
                  onError={(e) => {
                    e.target.src = cardData?.gender === "Female" ? "/user_placeholder_women.png" : "/user-placeholder.png";
                  }}
                />
              </div>

              <div className="pt-20 text-center">
                {cardData?.nameVisible && cardData?.name && (
                  <h3 style={getTextStyle("name")} className="mb-1">
                    {cardData?.name}
                  </h3>
                )}
                {cardData?.designationVisible && cardData?.designation && (
                  <p style={getTextStyle("designation")}>
                    {cardData?.designation}
                  </p>
                )}
              </div>

              <div className="mt-6 space-y-1 text-center">
                {cardData?.phoneVisible && cardData?.phone && (
                  <p style={getTextStyle("phone")}>{cardData.phone}</p>
                )}
                {cardData?.emailVisible && cardData?.email && (
                  <p style={getTextStyle("email")}>{cardData?.email}</p>
                )}
                {cardData?.employeeIdVisible && cardData?.employeeId && (
                  <p style={getTextStyle("employeeId")}>
                    {cardData?.employeeId}
                  </p>
                )}
              </div>

              <div
                className="mt-6 w-60 flex mx-auto border-t"
                style={{
                  borderTop: `2px solid ${cardData?.dividerColor || "#e5e7eb"}`,
                }}
              />
              <div className=" pt-4 mb-4">
                {cardData?.companyNameVisible && cardData?.companyName && (
                  <p
                    style={getTextStyle("companyName")}
                    className="text-center"
                  >
                    {cardData?.companyName}
                  </p>
                )}
              </div>

              {cardData?.qrCodeVisible && (
                <div className=" flex justify-center">
                  <img
                    src={cardData?.qrCodeUrl || "/sample_qr.svg"}
                    alt="QR Code"
                    width="100px"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Card Preview Modal */}
      {cardData && (
        <CardPreview
          id={isPublicEmployee ? authCtx.profile.publicEmployeeId : null}
          name={cardData?.name}
          isOpen={isPreviewOpen}
          cardData={cardData}
          onClose={() => setIsPreviewOpen(false)}
          isIndividual={true}
        />
      )}
    </div>
  );
};

export default CardCustomizer;
